import InvoiceCFeCreateModal from '@/components/Fiscal/Invoice/InvoiceCFe/InvoiceCFeCreateModal';
import {
  CompaniesInfiniteSelectField,
  DefaultRouteTabs,
  DeleteIcon,
  EditIcon,
  FloatingFormItem,
  InfiniteTable,
} from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  useTaxCouponList,
  useTaxCouponRemove,
} from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  TaxCoupon,
  TaxCouponStatus,
  TaxCouponStatusColor,
  TaxCouponStatusLabel,
} from '@/core/domain/coupon/coupons/coupons.types';
import { useDisclosure } from '@/hooks';
import Authorities from '@/types/Authority';
import { PageProps } from '@/types/PageProps';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Button, Col, Form, Modal, Row, Select, Space } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

const InvoiceCFeListPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();

  const [form] = Form.useForm<{
    companyId: number;
    status?: TaxCouponStatus;
  }>();
  const queryParams = Form.useWatch([], form);

  const couponRemove = useTaxCouponRemove();

  const { data, isLoading, fetchNextPage, isFetching, hasNextPage } =
    useTaxCouponList({ ...queryParams });
  const coupons = flatInfiniteData(data);

  const statusOptions = [
    {
      value: TaxCouponStatus.CANCELED,
      label: TaxCouponStatusLabel[TaxCouponStatus.CANCELED],
    },
    {
      value: TaxCouponStatus.EMITTED,
      label: TaxCouponStatusLabel[TaxCouponStatus.EMITTED],
    },
    {
      value: TaxCouponStatus.TYPING,
      label: TaxCouponStatusLabel[TaxCouponStatus.TYPING],
    },
  ];

  const [modalOpened, modalAction] = useDisclosure(false);

  function confirmRemove(item: TaxCoupon) {
    Modal.confirm({
      content: 'Deseja remover o cupom selecionado?',
      onOk: () => couponRemove.mutateAsync(item.id),
      okText: 'Remover',
      okType: 'danger',
    });
  }

  const columns: ColumnsType<TaxCoupon> = [
    {
      title: 'Destinatário',
      render: (item) => item?.recipient?.clientName || '-',
      width: 100,
    },
    {
      dataIndex: ['sefazBreakdown', 'cfeNumber'],
      title: 'Núm. Cupom',
      ellipsis: true,
      width: 200,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'status',
      title: 'Status',
      render: (_, item) => (
        <StatusTag
          label={TaxCouponStatusLabel[item.status]}
          color={TaxCouponStatusColor[item.status]}
        />
      ),
      width: 200,
    },
    {
      dataIndex: 'createdAt',
      title: 'Dt. Criação',
      render: (date) => moment(date).format('DD/MM/YYYY  HH:mm'),
      width: 100,
    },
    {
      title: 'Ação',
      align: 'right',
      width: 60,
      render: (_, item) => (
        <Space align="end">
          <EditIcon
            redirect={`/app/fiscal/cfe/${item.id}/${queryParams.companyId}/general`}
          />

          <DeleteIcon
            onClick={() => confirmRemove(item)}
            disabled={item.status !== TaxCouponStatus.TYPING}
          />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      route={route.parent}
      persistSearchParams={false}
      permissions={route.permissions}
      extra={
        <Button
          block
          type="primary"
          disabled={!hasPermissions([Authorities.FISCAL_INVOICE_ADD])}
          onClick={modalAction.open}
        >
          Cadastrar Cupom
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Form form={form} style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} style={{ width: '100%' }}>
          <Col span={24} md={8}>
            <FloatingFormItem name="companyId" label="Empresa">
              <CompaniesInfiniteSelectField allowClear={false} selectFirst />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6}>
            <FloatingFormItem name="status" label="Status">
              <Select options={statusOptions} allowClear />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <InfiniteTable
        dataSource={coupons}
        columns={columns}
        loading={isLoading}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetching={isFetching}
        scroll={{ x: true }}
      />

      <InvoiceCFeCreateModal open={modalOpened} onCancel={modalAction.close} />
    </DefaultRouteTabs>
  );
};

export default InvoiceCFeListPage;
